import { AlertTriangle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./result-block.i18n";

/**
 * The Initial · Adjustment · Final readout. QL-650 — the score is shown as a
 * plain number: no colour and no low/medium/high label.
 *
 * This is the only section that is not an input, which is why QL-212 places it
 * by the SCORE rather than by a scope: it renders on the goal page whenever any
 * input is captured per goal (where two goals visibly differ), and on the global
 * page only when all three are client-level.
 *
 * `finalScore` is the value to display — the container prefers the server's
 * persisted score (which carries the cap) once every question is answered, and
 * falls back to the live client-side computation while the client is still
 * choosing.
 */
export function ResultBlock({
  initialScore,
  adjustment,
  finalScore,
  showBreakdown,
  showCapWarning,
}: {
  initialScore: number | null;
  adjustment: number;
  finalScore: number | null;
  showBreakdown: boolean;
  showCapWarning: boolean;
}) {
  const t = useT(T);
  return (
    <div className="pt-4 border-t">
      <div className="text-sm font-medium text-muted-foreground mb-3">{t.riskAssessmentResult}</div>
      <div className="flex items-center gap-6 flex-wrap">
        <div className="flex flex-col" data-testid="risk-score-initial">
          <span className="text-xs text-muted-foreground">{t.initialScore}</span>
          <span className="text-xl font-semibold">{initialScore ?? "-"}</span>
        </div>
        {showBreakdown && (
          <div className="flex flex-col" data-testid="risk-score-adjustment">
            <span className="text-xs text-muted-foreground">{t.adjustmentLabel}</span>
            <span className="text-xl font-semibold tabular-nums">
              {adjustment > 0 ? `+${adjustment}` : adjustment}
            </span>
          </div>
        )}
        <div className="flex flex-col" data-testid="risk-score-final">
          <span className="text-xs text-muted-foreground">{t.finalScore}</span>
          <span className="flex items-center gap-1">
            <span className="text-2xl font-bold">{finalScore ?? "-"}</span>
            {showCapWarning && (
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      role="img"
                      aria-label={t.scoreCapped}
                      className="text-warning"
                      data-testid="risk-score-capped"
                    >
                      <AlertTriangle className="h-5 w-5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">{t.scoreCapped}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}
